import React, { useState, useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { calcEMI, formatINR, formatLakhs } from "../lib/calculators";

export const EMICalculator = ({ initialPrincipal = 500000, initialRate = 11, initialMonths = 36, compact = false, productLabel = "Loan" }) => {
  const [P, setP] = useState(initialPrincipal);
  const [R, setR] = useState(initialRate);
  const [N, setN] = useState(initialMonths);
  const { emi, total, interest } = useMemo(() => calcEMI(P, R, N), [P, R, N]);

  const data = [
    { name: "Principal", value: P, color: "#1088D4" },
    { name: "Interest", value: interest, color: "#F59E0B" },
  ];

  return (
    <div data-testid="emi-calculator" className={`bg-white border border-[#E2E8F0] rounded-2xl ${compact ? "p-5" : "p-6 sm:p-8"}`}>
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-6">
          <RangeRow label={`${productLabel} Amount`} value={formatLakhs(P)} min={50000} max={10000000} step={10000} val={P} set={setP} testid="emi-principal"/>
          <RangeRow label="Interest Rate" value={`${R.toFixed(2)}%`} min={6} max={28} step={0.1} val={R} set={setR} testid="emi-rate"/>
          <RangeRow label="Tenure" value={`${N} months`} min={6} max={84} step={1} val={N} set={setN} testid="emi-tenure"/>
        </div>
        <div className="flex flex-col items-center">
          <div className="w-full h-48">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" innerRadius={50} outerRadius={75} paddingAngle={2}>
                  {data.map((d,i) => <Cell key={i} fill={d.color}/>)}
                </Pie>
                <Tooltip formatter={(v) => formatINR(v)}/>
                <Legend verticalAlign="bottom" iconType="circle"/>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="w-full grid grid-cols-3 gap-2 mt-2 text-center">
            <Stat label="Monthly EMI" value={formatINR(emi)} accent/>
            <Stat label="Total Interest" value={formatINR(interest)}/>
            <Stat label="Total Payment" value={formatINR(total)}/>
          </div>
        </div>
      </div>
    </div>
  );
};

const RangeRow = ({ label, value, min, max, step, val, set, testid }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <label className="text-sm font-medium text-[#273B59]">{label}</label>
      <span className="text-sm font-bold text-[#1088D4]">{value}</span>
    </div>
    <input data-testid={testid} type="range" min={min} max={max} step={step} value={val} onChange={(e) => set(Number(e.target.value))} className="ol-slider w-full"/>
    <div className="flex justify-between text-[10px] text-[#94A3B8] mt-1">
      <span>{typeof min === "number" && min < 1000 ? min : formatLakhs(min)}</span>
      <span>{typeof max === "number" && max < 1000 ? max : formatLakhs(max)}</span>
    </div>
  </div>
);

const Stat = ({ label, value, accent }) => (
  <div className={`rounded-lg p-3 ${accent ? "bg-[#1088D4] text-white" : "bg-[#F7F9FC] text-[#273B59]"}`}>
    <div className={`text-[10px] uppercase tracking-wider ${accent ? "text-white/80" : "text-[#94A3B8]"}`}>{label}</div>
    <div className="mt-0.5 text-sm font-bold">{value}</div>
  </div>
);
